import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { featureFlagsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, ToggleLeft, ToggleRight, Flag, Users, X } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

export default function AdminFeatureFlagsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [emailInputs, setEmailInputs] = useState<Record<number, string>>({});

  const { data: flags, isLoading } = useQuery({
    queryKey: ['admin-feature-flags'],
    queryFn: featureFlagsApi.getAdminFlags,
  });

  const createMutation = useMutation({
    mutationFn: featureFlagsApi.createFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feature-flags'] });
      setShowForm(false);
      setForm({ name: '', description: '' });
      toast({ title: 'Feature flag created!' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: number; isEnabled: boolean }) =>
      featureFlagsApi.updateFlag(id, { isEnabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feature-flags'] });
      toast({ title: 'Flag updated!' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const addEmailMutation = useMutation({
    mutationFn: ({ id, emails, newEmail }: { id: number; emails: string[]; newEmail: string }) =>
      featureFlagsApi.updateFlag(id, { allowedEmails: [...emails, newEmail] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feature-flags'] });
      setEmailInputs({});
      toast({ title: 'Email added!' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const removeEmailMutation = useMutation({
    mutationFn: ({ id, emails, removeEmail }: { id: number; emails: string[]; removeEmail: string }) =>
      featureFlagsApi.updateFlag(id, { allowedEmails: emails.filter((e: string) => e !== removeEmail) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feature-flags'] });
      toast({ title: 'Email removed!' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: featureFlagsApi.deleteFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feature-flags'] });
      toast({ title: 'Flag deleted!' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const flagsList = Array.isArray(flags) ? flags : [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Feature Flags</h1>
          <p className="text-gray-500 text-sm mt-1">Control feature access for specific users before full release</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          <Plus className="w-4 h-4 mr-2" /> New Flag
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Input
                placeholder="Flag name (e.g. new_dashboard)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <Input
                placeholder="Description (optional)"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <Button onClick={() => createMutation.mutate(form)} disabled={!form.name}>
                Create
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-center py-8 text-gray-500">Loading...</p>
      ) : flagsList.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Flag className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No feature flags yet</p>
            <p className="text-sm mt-1">Create a flag to control feature access for specific users</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {flagsList.map((flag: any) => {
            const emails = JSON.parse(flag.allowedEmails || '[]');
            return (
              <Card key={flag.id}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Flag className="w-4 h-4" />
                        {flag.name}
                        {flag.isEnabled ? (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">LIVE FOR ALL</span>
                        ) : emails.length > 0 ? (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                            {emails.length} user{emails.length !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">OFF</span>
                        )}
                      </CardTitle>
                      {flag.description && <p className="text-sm text-gray-500 mt-1">{flag.description}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleMutation.mutate({ id: flag.id, isEnabled: !flag.isEnabled })}
                      >
                        {flag.isEnabled ? <ToggleRight className="w-4 h-4 mr-1 text-green-600" /> : <ToggleLeft className="w-4 h-4 mr-1" />}
                        {flag.isEnabled ? 'Disable' : 'Enable for All'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { if (confirm('Delete this flag?')) deleteMutation.mutate(flag.id); }}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-sm font-medium flex items-center gap-1">
                      <Users className="w-3 h-3" /> Allowed Emails {flag.isEnabled && '(flag is enabled for everyone)'}
                    </p>
                    {emails.map((email: string) => (
                      <div key={email} className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded text-sm">
                        <span>{email}</span>
                        <button
                          onClick={() => removeEmailMutation.mutate({ id: flag.id, emails, removeEmail: email })}
                          className="text-red-400 hover:text-red-600 ml-auto"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="user@example.com"
                        value={emailInputs[flag.id] || ''}
                        onChange={(e) => setEmailInputs({ ...emailInputs, [flag.id]: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && emailInputs[flag.id]) {
                            addEmailMutation.mutate({ id: flag.id, emails, newEmail: emailInputs[flag.id] });
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!emailInputs[flag.id]}
                        onClick={() => addEmailMutation.mutate({ id: flag.id, emails, newEmail: emailInputs[flag.id] })}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
